import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Feature {
  icon: string;
  title: string;
  description: string;
}

export default function WhiteLabelShowcase() {
  const features: Feature[] = [
  {
    icon: "PaintBrushIcon",
    title: "Custom Branding",
    description: "Apply your logo, colors, and brand identity across the entire platform"
  },
  {
    icon: "GlobeAltIcon",
    title: "Custom Domain",
    description: "Host the platform on your own domain with SSL certification"
  },
  {
    icon: "DevicePhoneMobileIcon",
    title: "Mobile Apps",
    description: "iOS and Android apps with your branding in app stores"
  },
  {
    icon: "EnvelopeIcon",
    title: "Email Templates",
    description: "Customized email notifications and confirmations"
  },
  {
    icon: "CreditCardIcon",
    title: "Payment Integration",
    description: "Your own payment gateway and merchant account"
  },
  {
    icon: "ChartBarIcon",
    title: "Analytics Dashboard",
    description: "Branded reporting and business intelligence tools"
  }];


  const benefits = [
  "Maintain complete brand consistency",
  "Build customer loyalty to your brand",
  "Control the entire user experience",
  "Increase brand recognition and trust",
  "Differentiate from competitors",
  "Own the customer relationship"];


  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="SparklesIcon" size={20} className="text-primary" />
            <span className="text-sm font-medium text-primary">White-Label Solution</span>
          </div>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Your Brand, Our Technology
          </h2>
          <p className="text-lg text-muted-foreground">
            Launch your own branded travel platform powered by our proven technology. Complete customization with zero technical complexity.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Left - Visual Showcase */}
          <div className="relative">
            <div className="relative bg-card rounded-2xl shadow-brand-lg overflow-hidden">
              {/* Browser Chrome */}
              <div className="bg-surface px-4 py-3 flex items-center space-x-2 border-b border-border">
                <div className="flex space-x-2">
                  <div className="w-3 h-3 rounded-full bg-error" />
                  <div className="w-3 h-3 rounded-full bg-warning" />
                  <div className="w-3 h-3 rounded-full bg-success" />
                </div>
                <div className="flex-1 bg-muted rounded px-3 py-1 text-xs text-muted-foreground">
                  yourbrand.com
                </div>
              </div>

              {/* Mock Interface */}
              <div className="p-6 space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between pb-4 border-b border-border">
                  <div className="flex items-center space-x-2">
                    <div className="w-10 h-10 bg-primary rounded-lg" />
                    <div className="font-heading font-bold text-foreground">Your Brand</div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-20 h-8 bg-muted rounded" />
                    <div className="w-24 h-8 bg-coral rounded" />
                  </div>
                </div>

                {/* Hero Section */}
                <div className="relative h-48 bg-gradient-to-br from-primary to-secondary rounded-lg overflow-hidden">
                  <AppImage
                    src="https://images.unsplash.com/photo-1525849306000-cc26ceb5c1d7"
                    alt="Aerial view of tropical beach with turquoise water and white sand"
                    className="w-full h-full object-cover opacity-30" />

                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-white">
                      <div className="font-heading text-2xl font-bold mb-2">Your Tagline Here</div>
                      <div className="text-sm opacity-90">Powered by your brand</div>
                    </div>
                  </div>
                </div>

                {/* Content Cards */}
                <div className="grid grid-cols-3 gap-3">
                  {[1, 2, 3].map((i) =>
                  <div key={i} className="bg-surface rounded-lg p-3">
                      <div className="w-full h-20 bg-muted rounded mb-2" />
                      <div className="h-3 bg-muted rounded mb-1" />
                      <div className="h-3 bg-muted rounded w-2/3" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Floating Badge */}
            <div className="absolute -bottom-4 -right-4 bg-success text-white px-6 py-3 rounded-lg shadow-xl">
              <div className="flex items-center space-x-2">
                <Icon name="CheckBadgeIcon" size={24} />
                <div>
                  <div className="font-heading font-semibold">100% Branded</div>
                  <div className="text-xs opacity-90">Your Identity</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Features */}
          <div className="space-y-6">
            <h3 className="font-heading text-2xl font-bold text-foreground mb-6">
              Complete Customization Features
            </h3>

            <div className="grid gap-4">
              {features.map((feature, index) =>
              <div
                key={index}
                className="flex items-start space-x-4 bg-card p-4 rounded-xl shadow-brand hover:shadow-brand-lg transition-all duration-300">

                  <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name={feature.icon as any} size={24} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-foreground mb-1">
                      {feature.title}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {feature.description}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="bg-card rounded-2xl shadow-brand-lg p-8 lg:p-12">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left - Benefits List */}
            <div>
              <h3 className="font-heading text-2xl font-bold text-foreground mb-6">
                Why Choose White-Label?
              </h3>
              <div className="space-y-4">
                {benefits.map((benefit, index) =>
                <div key={index} className="flex items-start space-x-3">
                    <Icon name="CheckCircleIcon" size={24} className="text-success flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{benefit}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Right - Stats & CTA */}
            <div className="space-y-8">
              {/* Stats */}
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-surface rounded-xl p-6">
                  <div className="text-3xl font-heading font-bold text-primary mb-2">2-4</div>
                  <div className="text-sm text-muted-foreground">Weeks to Launch</div>
                </div>
                <div className="bg-surface rounded-xl p-6">
                  <div className="text-3xl font-heading font-bold text-primary mb-2">100%</div>
                  <div className="text-sm text-muted-foreground">Your Branding</div>
                </div>
                <div className="bg-surface rounded-xl p-6">
                  <div className="text-3xl font-heading font-bold text-primary mb-2">24/7</div>
                  <div className="text-sm text-muted-foreground">Support Included</div>
                </div>
                <div className="bg-surface rounded-xl p-6">
                  <div className="text-3xl font-heading font-bold text-primary mb-2">∞</div>
                  <div className="text-sm text-muted-foreground">Customization</div>
                </div>
              </div>

              {/* CTA */}
              <div className="bg-gradient-to-br from-primary to-secondary rounded-xl p-6 text-white">
                <h4 className="font-heading text-xl font-bold mb-2">
                  Enterprise White-Label Package
                </h4>
                <p className="text-white/90 text-sm mb-4">
                  Get a custom quote for your white-label solution with dedicated support and implementation.
                </p>
                <button className="w-full bg-coral hover:bg-[#FF5722] text-white py-3 rounded-lg font-heading font-semibold transition-all duration-300 shadow-lg hover:shadow-xl">
                  Request White-Label Demo
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}